import type { IPrimitivePaneView, ISeriesPrimitive, Time } from 'lightweight-charts';

/**
 * Tells a consumer when a pane's value -> y mapping moved. Draws nothing.
 *
 * lightweight-charts has no price-scale subscription, but every primitive gets
 * updateAllViews() on each redraw of its pane: autoscale after new data, a
 * visible-range change, a vertical drag on the axis, a pane resize. This one
 * samples `signature()` (e.g. where a few fixed values land on each scale of
 * interest) and calls `onChange` once per animation frame at most, only when
 * the signature differs from the last one it reported. The callback does not
 * redraw the pane it watches, so it cannot loop on itself.
 *
 * Unlike PriceScaleWatcherPrimitive it can watch several scales (a series on
 * its own hidden scale included) and needs no data on the attached series.
 */
export class PaneGeometryWatcherPrimitive implements ISeriesPrimitive<Time> {
  private _last: string | null = null;
  private _raf: number | null = null;
  private _attached = false;

  constructor(
    private readonly _signature: () => string | null,
    private readonly _onChange: () => void,
  ) {}

  attached(): void {
    this._attached = true;
    this._last = null;
  }

  detached(): void {
    this._attached = false;
    this.cancel();
  }

  paneViews(): readonly IPrimitivePaneView[] {
    return [];
  }

  updateAllViews(): void {
    this.check();
  }

  /** Re-sample now (also used by a visible-range subscription). */
  check(): void {
    if (!this._attached || this._raf !== null) return;
    const schedule = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (cb: FrameRequestCallback) => setTimeout(() => cb(0), 16) as unknown as number;
    this._raf = schedule(() => {
      this._raf = null;
      if (!this._attached) return;
      const sig = this._signature();
      if (sig === this._last) return;
      this._last = sig;
      this._onChange();
    });
  }

  cancel(): void {
    if (this._raf === null) return;
    if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(this._raf);
    else clearTimeout(this._raf as unknown as ReturnType<typeof setTimeout>);
    this._raf = null;
  }
}
